import {Action, action, Thunk, thunk} from 'easy-peasy';
import {buildURL} from '@/utils/urlBuilder';

export enum SidebarMenuSections {
    Insights = 'Insights',
    Discover = 'Discover',
    Tasks = 'Tasks',
    Conversations = 'Conversations',
    Customers = 'Customers',
    Agents = 'Agents',
    Teams = 'Teams',
    Admin = 'Admin',
    Reports = 'Reports',
    Stories = 'Stories',
    Settings = 'Settings',
}

export interface TabConfig {
    key: string;
    title: string;
    icon?: string;
    route: string;
    viewComponent: string;
    order?: number;
}

export interface MenuItemMeta {
    title: string;
    icon?: string;
    order: number;
    accessGroups: string[];
    visible?: boolean;
    showBreadcrumb?: boolean;
    showDateRangePicker?: boolean;
    showComparisonDatePicker?: boolean;
    hasTabNavigation?: boolean;
    showTabNavigation?: boolean;
    defaultTab?: string;
    hasDynamicChildren?: boolean;
    dynamicChildrenApi?: string;
    wildcardRoute?: boolean;
    parametrized?: boolean;
    parameters?: string[];
    defaultParameters?: Record<string, string>;
    routes?: {
        list?: string;
        detail?: string;
        base?: string;
    };
    api?: {
        list?: string;
        detail?: string;
    };
    viewComponent?: string;
    detailViewComponent?: string;
    tabs?: TabConfig[];
    breadcrumbs?: Array<{
        key: string;
        text: string;
        href: string;
    }>;
    activeRoutes?: string[];
}

export interface MenuItem {
    path: string;
    name: string;
    meta: MenuItemMeta;
    children?: MenuItem[];
    dynamicChildren?: DynamicMenuChild[];
}

export interface DynamicMenuChild {
    id: string;
    title: string;
    route: string;
    icon?: string;
    storyTitle?: string;
    storyboardTitle?: string;
    storyHashid?: string;
    storyboardId?: number;
}

export interface MenuSection {
    name: string;
    meta: MenuItemMeta;
    items: MenuItem[];
}

export interface SidebarMenuModel {
    openSection: SidebarMenuSections | '';
    setOpenSection: Action<SidebarMenuModel, SidebarMenuSections | ''>;

    // this is liberal because it can be a full URL (with params)
    activeLinkRoute: string | '';
    setActiveLinkRoute: Action<SidebarMenuModel, string | ''>;

    // Menu structure data
    menuStructure: MenuSection[];
    setMenuStructure: Action<SidebarMenuModel, MenuSection[]>;
    
    // Loading states
    isLoading: boolean;
    setIsLoading: Action<SidebarMenuModel, boolean>;
    
    error: string | null;
    setError: Action<SidebarMenuModel, string | null>;
    
    // Cache timestamp
    lastUpdated: number;
    setLastUpdated: Action<SidebarMenuModel, number>;
    
    // Dynamic children cache
    dynamicChildrenCache: Record<string, DynamicMenuChild[]>;
    setDynamicChildren: Action<SidebarMenuModel, { path: string; children: DynamicMenuChild[] }>;
    
    dynamicChildrenLoading: Record<string, boolean>;
    setDynamicChildrenLoading: Action<SidebarMenuModel, { path: string; loading: boolean }>;
    
    // Actions
    loadMenuStructure: Thunk<SidebarMenuModel>;
    loadDynamicChildren: Thunk<SidebarMenuModel, string>;
    clearMenuCache: Action<SidebarMenuModel>;
}

export default <SidebarMenuModel>{
    openSection: '',
    setOpenSection: action((state, sectionName) => {
        state.openSection = sectionName;
    }),
    
    // NB Does not always overlap with activeRoute in activePage
    activeLinkRoute: '',
    setActiveLinkRoute: action((state, linkRoute) => {
        state.activeLinkRoute = linkRoute;
    }),
    
    // Menu structure data
    menuStructure: [],
    setMenuStructure: action((state, menuStructure) => {
        state.menuStructure = menuStructure;
    }),
    
    // Loading states
    isLoading: false,
    setIsLoading: action((state, isLoading) => {
        state.isLoading = isLoading;
    }),
    
    error: null,
    setError: action((state, error) => {
        state.error = error;
    }),
    
    // Cache timestamp
    lastUpdated: 0,
    setLastUpdated: action((state, timestamp) => {
        state.lastUpdated = timestamp;
    }),
    
    // Dynamic children cache
    dynamicChildrenCache: {},
    setDynamicChildren: action((state, {path, children}) => {
        state.dynamicChildrenCache[path] = children;
    }),
    
    dynamicChildrenLoading: {},
    setDynamicChildrenLoading: action((state, {path, loading}) => {
        state.dynamicChildrenLoading[path] = loading;
    }),
    
    // Load menu structure from API
    loadMenuStructure: thunk(async (actions, payload, {getState}) => {
        const state = getState();
        
        // Check if we have recent cached data (5 minutes)
        const cacheTimeout = 5 * 60 * 1000;
        if (state.menuStructure.length > 0 && 
            state.lastUpdated > 0 && 
            Date.now() - state.lastUpdated < cacheTimeout) {
            return;
        }
        
        try {
            actions.setIsLoading(true);
            actions.setError(null);
            
            const response = await fetch(buildURL('systemCompleteMenuStructure'));
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();
            
            if (data.status === 'success') {
                actions.setMenuStructure(data.menu_structure);
                actions.setLastUpdated(Date.now());
            } else {
                throw new Error(data.message || 'Failed to load menu structure');
            }
        } catch (error) {
            actions.setError(error instanceof Error ? error.message : 'Unknown error');
            
            // Set fallback menu structure if none exists
            if (state.menuStructure.length === 0) {
                actions.setMenuStructure([]);
            }
        } finally {
            actions.setIsLoading(false);
        }
    }),
    
    // Load dynamic children for a menu item or section
    loadDynamicChildren: thunk(async (actions, itemPath, {getState}) => {
        const state = getState();
        
        // Check if already loading
        if (state.dynamicChildrenLoading[itemPath]) {
            return;
        }
        
        // Check if we have cached data
        if (state.dynamicChildrenCache[itemPath]) {
            return;
        }
        
        // Find the menu item or section to get its API endpoint
        let apiEndpoint: string | null = null;
        
        // First check if this is a section
        for (const section of state.menuStructure) {
            if (section.name === itemPath && section.meta.hasDynamicChildren && section.meta.dynamicChildrenApi) {
                apiEndpoint = section.meta.dynamicChildrenApi;
                break;
            }
            
            // Otherwise check items within sections
            const item = section.items.find(item => item.path === itemPath);
            if (item && item.meta.hasDynamicChildren && item.meta.dynamicChildrenApi) {
                apiEndpoint = item.meta.dynamicChildrenApi;
                break;
            }
        }
        
        if (!apiEndpoint) {
            return;
        }
        
        try {
            actions.setDynamicChildrenLoading({path: itemPath, loading: true});
            
            const response = await fetch(apiEndpoint);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();
            
            if (data.status === 'success') {
                actions.setDynamicChildren({path: itemPath, children: data.items});
            } else {
                throw new Error(data.message || 'Failed to load dynamic children');
            }
        } catch (error) {
            // Set empty array on error
            actions.setDynamicChildren({path: itemPath, children: []});
        } finally {
            actions.setDynamicChildrenLoading({path: itemPath, loading: false});
        }
    }),
    
    // Clear menu cache
    clearMenuCache: action((state) => {
        state.menuStructure = [];
        state.lastUpdated = 0;
        state.error = null;
        state.dynamicChildrenCache = {};
        state.dynamicChildrenLoading = {};
    }),
};
